import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { registerSuperAdmin } from '../../../services/authService'

const SuperAdminRegister = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    secretKey: '',
    fullName: '',
    email: '',
    employeeId: '',
    password: '',
    confirmPassword: ''
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('') // Clear error when user types
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validate secret key first
    const envSecret = import.meta.env.VITE_SUPER_ADMIN_SECRET
    if (!envSecret || envSecret === 'change_this_to_a_secure_random_string') {
      setError('Super Admin secret key not configured. Contact system administrator.')
      return
    }

    if (formData.secretKey !== envSecret) {
      setError('Invalid secret key. Super Admin registration requires authorization.')
      return
    }

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    setLoading(true)

    const result = await registerSuperAdmin(
      formData.email,
      formData.password,
      formData.fullName,
      formData.employeeId
    )

    setLoading(false)

    if (result.success) {
      navigate('/dashboard')
    } else {
      setError(result.error)
    }
  }

  return (
    <div className="psyber-container psyber-portal-container">
      {/* Background Effects */}
      <div className="psyber-bg-effects">
        <div className="psyber-orb psyber-orb-1"></div>
        <div className="psyber-orb psyber-orb-2"></div>
        <div className="psyber-grid"></div>
      </div>

      {/* Registration Card */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh'
      }}>
        <div className="psyber-card" style={{
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}>
          {/* Logo/Title */}
          <div style={{ marginBottom: '2rem' }}>
            <h1 className="psyber-title-gradient" style={{ marginBottom: '0.5rem' }}>
              Super Admin Setup
            </h1>
            <p style={{ color: 'var(--psyber-text-secondary)', fontSize: 'var(--psyber-font-size-sm)' }}>
              Create the first administrator account for PsyberNova Portal
            </p>
          </div>

          {/* Authorization Warning */}
          <div style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            background: 'var(--psyber-error-bg)',
            color: 'var(--psyber-error)',
            borderRadius: 'var(--psyber-radius-md)',
            border: '1px solid var(--psyber-error)',
            fontSize: 'var(--psyber-font-size-sm)',
            textAlign: 'left'
          }}>
            <i className="fas fa-lock"></i> <strong>Authorization Required:</strong> You must enter the secret key to create a Super Admin account.
          </div>

          {/* Warning Notice */}
          <div style={{
            padding: '0.75rem',
            marginBottom: '1.5rem',
            background: 'var(--psyber-warning-bg)',
            color: 'var(--psyber-warning)',
            borderRadius: 'var(--psyber-radius-md)',
            border: '1px solid var(--psyber-warning)',
            fontSize: 'var(--psyber-font-size-sm)',
            textAlign: 'left'
          }}>
            <i className="fas fa-shield-halved"></i> <strong>One-time setup:</strong> This creates the first admin account with full system access.
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
            {/* Error Message */}
            {error && (
              <div style={{
                padding: '0.75rem',
                marginBottom: '1rem',
                background: 'var(--psyber-error-bg)',
                color: 'var(--psyber-error)',
                borderRadius: 'var(--psyber-radius-md)',
                border: '1px solid var(--psyber-error)',
                fontSize: 'var(--psyber-font-size-sm)'
              }}>
                <i className="fas fa-exclamation-circle"></i> {error}
              </div>
            )}

            {/* Secret Key - FIRST FIELD */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--psyber-error-bg)',
              borderRadius: 'var(--psyber-radius-md)',
              border: '2px solid var(--psyber-error)'
            }}>
              <label htmlFor="secretKey" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-error)',
                fontSize: 'var(--psyber-font-size-base)'
              }}>
                <i className="fas fa-key"></i> Super Admin Secret Key *
              </label>
              <input
                type="password"
                id="secretKey"
                name="secretKey"
                className="psyber-input"
                placeholder="Enter the secret key from .env file"
                value={formData.secretKey}
                onChange={handleChange}
                required
                autoComplete="off"
                style={{
                  border: '2px solid var(--psyber-error)',
                  fontFamily: 'monospace',
                  fontSize: 'var(--psyber-font-size-base)'
                }}
              />
              <p style={{
                marginTop: '0.5rem',
                marginBottom: 0,
                fontSize: 'var(--psyber-font-size-xs)',
                color: 'var(--psyber-error)',
                fontWeight: 600
              }}>
                Contact system administrator for the secret key
              </p>
            </div>

            {/* Full Name */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="fullName" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                className="psyber-input"
                placeholder="John Doe"
                value={formData.fullName}
                onChange={handleChange}
                required
                autoComplete="name"
              />
            </div>

            {/* Employee ID */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="employeeId" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Employee ID
              </label>
              <input
                type="text"
                id="employeeId"
                name="employeeId"
                className="psyber-input"
                placeholder="EMP001"
                value={formData.employeeId}
                onChange={handleChange}
                required
              />
            </div>

            {/* Email */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="email" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="psyber-input"
                placeholder="admin@company.com"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="password" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                className="psyber-input"
                placeholder="Minimum 6 characters"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                minLength={6}
              />
            </div>

            {/* Confirm Password */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="confirmPassword" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className="psyber-input"
                placeholder="Re-enter password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="psyber-btn psyber-btn-primary"
              disabled={loading}
              style={{ width: '100%' }}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Creating Admin Account...
                </>
              ) : (
                <>
                  <i className="fas fa-user-shield"></i>
                  Create Super Admin
                </>
              )}
            </button>
          </form>

          {/* Footer Links */}
          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid var(--psyber-card-border)',
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text-secondary)'
          }}>
            <p>
              Already have an account?{' '}
              <Link to="/login" style={{
                color: 'var(--psyber-primary)',
                fontWeight: 600
              }}>
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SuperAdminRegister
